import React, {useEffect, useMemo, useState} from 'react'
import {useNavigate, useParams, useSearchParams} from 'react-router-dom'
import moment from 'moment'
import {DigitalVosFormPanel} from '../UI/appointment-management/components/digital-vos-form/DigitalVosFormPanel'
import {fetchDigitalVosFormPayload} from '../UI/appointment-management/components/digital-vos-form/fetchDigitalVosFormPayload'
import {bootstrapJwtAuth, getBearerToken} from '../UI/appointment-management/components/digital-vos-form/jwtBootstrap'
import type {BuildDigitalVosFormInput} from '../UI/appointment-management/components/digital-vos-form/digitalVosFormTypes'

type PageState = 'auth' | 'loading' | 'ready' | 'error'

export const DigitalVosViewPage: React.FC = () => {
  const {appointmentId = ''} = useParams<{appointmentId: string}>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const embedded = searchParams.get('embedded') === '1'
  const tokenFromUrl = searchParams.get('token') ?? searchParams.get('jwt') ?? ''

  const [pageState, setPageState] = useState<PageState>('auth')
  const [errorMessage, setErrorMessage] = useState('')
  const [formInput, setFormInput] = useState<BuildDigitalVosFormInput | null>(null)
  const [appointmentCode, setAppointmentCode] = useState('')

  const fileName = useMemo(
    () =>
      appointmentCode
        ? `VOS Form ${appointmentCode}-${moment().format('MM/DD/YYYY')}.pdf`
        : `VOS Form ${appointmentId}-${moment().format('MM/DD/YYYY')}.pdf`,
    [appointmentCode, appointmentId]
  )

  useEffect(() => {
    if (tokenFromUrl.trim()) {
      bootstrapJwtAuth(tokenFromUrl)
      if (embedded) {
        const nextParams = new URLSearchParams(searchParams)
        nextParams.delete('token')
        nextParams.delete('jwt')
        const qs = nextParams.toString()
        navigate(
          {pathname: `/digital-vos/view/${appointmentId}`, search: qs ? `?${qs}` : ''},
          {replace: true}
        )
      }
    }

    if (!getBearerToken()) {
      setErrorMessage('Authentication required. Open this page from the mobile app or sign in to Connect.')
      setPageState('error')
      return
    }

    if (!appointmentId) {
      setErrorMessage('Missing appointment ID.')
      setPageState('error')
      return
    }

    let cancelled = false

    const load = async () => {
      setPageState('loading')
      const result = await fetchDigitalVosFormPayload(appointmentId)
      if (cancelled) return

      if (!result.ok) {
        setErrorMessage(result.message)
        setPageState('error')
        return
      }

      setFormInput(result.input)
      setAppointmentCode(result.appointmentCode)
      setPageState('ready')
    }

    load()

    return () => {
      cancelled = true
    }
  }, [appointmentId, tokenFromUrl, embedded, navigate, searchParams])

  if (pageState === 'auth' || pageState === 'loading') {
    return (
      <div className='min-vh-100 d-flex flex-column align-items-center justify-content-center bg-light'>
        <span className='spinner-border text-primary mb-3' role='status' />
        <p className='text-muted mb-0'>Loading digital VOS form…</p>
      </div>
    )
  }

  if (pageState === 'error' || !formInput) {
    return (
      <div className='min-vh-100 d-flex flex-column align-items-center justify-content-center bg-light px-4'>
        <div className='text-center' style={{maxWidth: 420}}>
          <i className='bi bi-exclamation-triangle text-warning fs-1 mb-3 d-block' />
          <h5 className='mb-2'>Unable to load VOS form</h5>
          <p className='text-muted mb-0'>{errorMessage || 'An unexpected error occurred.'}</p>
        </div>
      </div>
    )
  }

  return (
    <DigitalVosFormPanel
      standalone
      input={formInput}
      fileName={fileName}
      onDownloadError={(message) => setErrorMessage(message)}
    />
  )
}

export default DigitalVosViewPage
